import React, {
  ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
} from "react";
import ReactDOM from "react-dom";
import "../../styles/AccessibleDialog.scss";

/**
 * A modal dialog that behaves like one for keyboard and screen-reader users, which the shared
 * `Modal` does not: Esc and × close it, Tab stays inside it, focus goes back to whatever opened
 * it, and it is announced as a modal dialog named by its title. On a small screen the dialog
 * keeps its header and scrolls its body.
 *
 * `closeDisabled` holds it open — for the moment a request it started is on its way and the
 * page is about to be left, when closing would only hide what is happening.
 */

interface AccessibleDialogProps {
  title: ReactNode;
  onClose: () => void;
  closeDisabled?: boolean;
  className?: string;
  children: ReactNode;
}

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

const AccessibleDialog: React.FC<AccessibleDialogProps> = ({
  title,
  onClose,
  closeDisabled = false,
  className = "",
  children,
}) => {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  // The listeners below are installed once; these keep them reading the current props.
  const onCloseRef = useRef(onClose);
  const closeDisabledRef = useRef(closeDisabled);
  useEffect(() => {
    onCloseRef.current = onClose;
    closeDisabledRef.current = closeDisabled;
  });
  // A press that starts inside the dialog and ends on the backdrop (selecting text, say) is not
  // a click on the backdrop.
  const pressStartedOnBackdrop = useRef(false);

  // Layout rather than passive: the opener has to be read before anything inside the dialog can
  // take focus, and focus has to move before the user can press a key. One effect for all of it,
  // because the order on the way out matters: the listeners go before focus is handed back, or
  // the focus trap would pull it straight back into a dialog that is being removed.
  useLayoutEffect(() => {
    const opener =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    const focusableIn = (dialog: HTMLElement) =>
      Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE));

    const onKeyDown = (event: KeyboardEvent) => {
      const dialog = dialogRef.current;
      if (!dialog) return;

      if (event.key === "Escape") {
        event.preventDefault();
        if (!closeDisabledRef.current) onCloseRef.current();
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = focusableIn(dialog);
      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (!dialog.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && (active === first || active === dialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    // Focus that lands outside anyway — a script, a click the backdrop let through — is pulled
    // back in.
    const onFocusIn = (event: FocusEvent) => {
      const dialog = dialogRef.current;
      if (dialog && !dialog.contains(event.target as Node)) dialog.focus();
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("focusin", onFocusIn);
    dialogRef.current?.focus();

    // The page behind stays where it is while the dialog is up.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("focusin", onFocusIn);
      document.body.style.overflow = previousOverflow;
      if (opener && opener !== document.body && opener.isConnected) {
        opener.focus();
      }
    };
  }, []);

  return ReactDOM.createPortal(
    <div
      className="iiq-dialog-overlay"
      onMouseDown={(event) => {
        pressStartedOnBackdrop.current = event.target === event.currentTarget;
      }}
      onClick={(event) => {
        const onBackdrop =
          pressStartedOnBackdrop.current &&
          event.target === event.currentTarget;
        pressStartedOnBackdrop.current = false;
        if (onBackdrop && !closeDisabled) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className={`iiq-dialog ${className}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className="iiq-dialog-header">
          <h2 id={titleId} className="iiq-dialog-title">
            {title}
          </h2>
          <button
            type="button"
            className="iiq-dialog-close"
            aria-label="Close"
            onClick={onClose}
            disabled={closeDisabled}
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
        <div className="iiq-dialog-body">{children}</div>
      </div>
    </div>,
    document.body,
  );
};

export default AccessibleDialog;
